import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  IconButton,
  useDisclosure,
  ButtonGroup,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { crm } from "../../theme";
import { FaPlus, FaEye, FaPencilAlt, FaTrash } from "react-icons/fa";
import { DateTime } from "luxon";
import { Tag } from "../../models/Filter";
import Layout from "../../components/Layout";
import TableWithPagination from "../../components/TableWithPagination";
import ReportPreviewModal from "./components/reportPreviewModal";
import NewReportModal, {
  ReportFilters,
  emptyFilters,
} from "./components/newReportModal";
import { getProcesses } from "../../services/processService";
import {
  getReports,
  saveReport,
  updateReport,
  getReportById,
  deleteReport,
} from "../../services/reportService";
import { useCompany } from "../../contexts/CompanyContext";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

import { useNavigate } from "react-router-dom";
// ── Tipos do backend ──────────────────────────────────────────────

interface BackendDateFilter {
  from: string | null;
  to: string | null;
}

interface BackendFilters {
  dates: {
    openingDate: BackendDateFilter | null;
    arrivalDate: BackendDateFilter | null;
    shippingDate: BackendDateFilter | null;
    customsClearanceDate: BackendDateFilter | null;
    nfIssued: BackendDateFilter | null;
    registrationDate: BackendDateFilter | null;
  };
  text: {
    cvfRef: string | null;
    client: string | null;
    exporter: string | null;
    status: string | null;
    responsible: string | null;
    incoterm: string | null;
    channel: string | null;
    di: string | null;
    li: string | null;
    blawb: string | null;
    containerNumber: string | null;
    ncm: string | null;
  };
}

interface BackendReport {
  id: string;
  name: string;
  createdAt?: string;
  fields: string[];
  filters: BackendFilters;
}

// ── Tipos do front ────────────────────────────────────────────────

export interface SavedFilters {
  text: Record<string, string>;
  dates: Record<string, { from: string; to: string }>;
}

export interface ReportItem {
  id: string;
  name: string;
  createdAt: string;
  fields: string[];
  filters?: SavedFilters;
}

// ── Mapeamento backend → front ────────────────────────────────────

const dateRange = (f: BackendDateFilter | null) => ({
  from: f?.from ?? "",
  to: f?.to ?? "",
});

const mapBackendToFront = (r: BackendReport): ReportItem => ({
  id: r.id,
  name: r.name,
  createdAt: r.createdAt ?? "",
  fields: r.fields ?? [],
  filters: {
    text: {
      CVFRef: r.filters?.text?.cvfRef ?? "",
      Client: r.filters?.text?.client ?? "",
      Exporter: r.filters?.text?.exporter ?? "",
      Status: r.filters?.text?.status ?? "",
      Responsible: r.filters?.text?.responsible ?? "",
      Incoterm: r.filters?.text?.incoterm ?? "",
      Channel: r.filters?.text?.channel ?? "",
      Di: r.filters?.text?.di ?? "",
      Li: r.filters?.text?.li ?? "",
      blAwb: r.filters?.text?.blawb ?? "",
      ContainerNumber: r.filters?.text?.containerNumber ?? "",
      Ncm: r.filters?.text?.ncm ?? "",
    },
    dates: {
      "OpeningDate": dateRange(r.filters?.dates?.openingDate),
      "ArrivalDate": dateRange(r.filters?.dates?.arrivalDate),
      "ShippingDate": dateRange(r.filters?.dates?.shippingDate),
      "CustomsClearanceDate": dateRange(r.filters?.dates?.customsClearanceDate),
      "NfIssued": dateRange(r.filters?.dates?.nfIssued),
      "RegistrationDate": dateRange(r.filters?.dates?.registrationDate),
    },
  },
});

// ── Componente ────────────────────────────────────────────────────

const ReportList = () => {
  const { currentCompany, currentEmulation } = useCompany();
  const toast = useToast();
  const auth = useAuthUser<any>();
  const navigate = useNavigate();
  const {
    isOpen: isOpenPreview,
    onOpen: onOpenPreview,
    onClose: onClosePreview,
  } = useDisclosure();
  const {
    isOpen: isOpenNew,
    onOpen: onOpenNew,
    onClose: onCloseNew,
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();

  const [selectedReport, setSelectedReport] = useState<
    ReportItem | undefined
  >();
  const [editingReport, setEditingReport] = useState<ReportItem | undefined>();
  const [page, setPage] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tags] = useState<Tag[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const hasFetched = useRef(false);

  const fetchReports = async (
    localPageSize?: number,
    localPageNumber?: number,
  ) => {
    setLoading(true);
    try {
      const localPs = localPageSize ?? pageSize;
      const localPn = localPageNumber ?? pageIndex;

      const response = await getReports({
        pageSize: localPs,
        pageNumber: localPn,
      });
      const raw: BackendReport[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setPage(raw.map(mapBackendToFront));
    } catch (err) {
      console.log("error fetching reports", err);
      setPage([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.position < 2) navigate("/");
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReports();
    }
  }, [auth]);

  const handleOpenDetail = async (report: ReportItem) => {
    try {
      setLoading(true);

      const response = await getReportById(report.id);

      const backendReport: BackendReport = response?.data ?? response;

      if (!backendReport) {
        toast({
          title: "Erro ao carregar relatório.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setSelectedReport(mapBackendToFront(backendReport));
      onOpenPreview();
    } catch (error) {
      console.log("error loading report", error);

      toast({
        title: "Erro ao carregar relatório.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = async (report: ReportItem) => {
    try {
      setLoading(true);

      const response = await getReportById(report.id);

      const backendReport: BackendReport = response?.data ?? response;

      if (!backendReport) {
        toast({
          title: "Erro ao carregar relatório.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      const mappedReport = mapBackendToFront(backendReport);
      
      setEditingReport(mappedReport);

      onOpenEdit();
    } catch (error) {
      console.log("error loading report", error);

      toast({
        title: "Erro ao carregar relatório.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (
    name: string,
    fields: string[],
    filters: ReportFilters,
  ) => {
    try {
      const response = await saveReport({ name, fields, filters });
      if (response?.data?.success) {
        toast({
          title: "Relatório salvo.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        await fetchReports();
        onCloseNew();
      } else {
        toast({
          title: "Erro ao salvar relatório.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch {
      toast({
        title: "Erro ao salvar relatório.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdate = async (
    name: string,
    fields: string[],
    filters: ReportFilters,
  ) => {
    try {
      if (!editingReport?.id) return;
      const response = await updateReport({ id: editingReport.id, name, fields, filters });
      if (response?.data?.success) {
        toast({
          title: "Relatório atualizado.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        await fetchReports();
        onCloseEdit();
      } else {
        toast({
          title: "Erro ao atualizar relatório.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch {
      toast({
        title: "Erro ao atualizar relatório.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;
    try {
      setLoading(true);

      const response = await deleteReport(reportToDelete);

      if (response?.data?.success) {
        toast({
          title: "Relatório excluído.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        await fetchReports();
      } else {
        toast({
          title: "Erro ao excluir relatório.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log("error deleting report", error);

      toast({
        title: "Erro ao excluir relatório.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setReportToDelete(null);
    }
  };

  const toReportFilters = (saved?: SavedFilters): ReportFilters => {
    if (!saved) return emptyFilters();
    return {
      text: { ...emptyFilters().text, ...saved.text },
      dates: { ...emptyFilters().dates, ...saved.dates },
    };
  };

  const get = async () => ({ data: page, totalCount: page.length });
  const [reportToDelete, setReportToDelete] = useState<string | null>(null);

  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Box height="100%">
      <Layout>
        <Flex
          flexDirection="column"
          alignItems="center"
          backgroundColor="var(--main-bg-color)"
          pb="48px"
        >
          <Flex width="90%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box>
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">Relatórios</Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">Crie e gerencie relatórios personalizados.</Text>
            </Box>
          </Flex>

          <Flex w="90%" justifyContent="flex-end" mb="24px">
            <Button
              color="white"
              borderRadius="10px"
              backgroundColor="var(--icon-color)"
              _hover={{ backgroundColor: crm.brandDark }}
              onClick={onOpenNew}
            >
              <Flex justifyContent="center" alignItems="center" gap="8px">
                <FaPlus color="white" /> Novo Relatório
              </Flex>
            </Button>
          </Flex>

          <Flex
            width="90%"
            backgroundColor="white"
            borderRadius="12px"
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            overflow="hidden"
            mb="32px"
            flexDirection="column"
          >
            <Box p="32px">
              {loading ? (
                <Flex justifyContent="center" py="48px">
                  <Spinner color="var(--icon-color)" />
                </Flex>
              ) : (
                <TableWithPagination
                  loading={false}
                  outerTotal={page.length}
                  columns={[
                    {
                      label: "Título",
                      sortable: true,
                      index: "name",
                      data: (r: ReportItem) => (
                        <Text fontWeight="medium">{r.name}</Text>
                      ),
                    },                    
                    {
                      label: "Data",
                      sortable: true,
                      index: "createdAt",
                      data: (r: ReportItem) => (
                        <Text color={crm.textMuted}>
                          {r.createdAt
                            ? DateTime.fromISO(r.createdAt).toFormat(
                                "dd/LL/yyyy HH:mm",
                              )
                            : "—"}
                        </Text>
                      ),
                    },
                    {
                      label: "Campos",
                      data: (r: ReportItem) => (
                        <Text color={crm.textFaint} fontSize="sm">
                          {r.fields.length} campo
                          {r.fields.length !== 1 ? "s" : ""}
                        </Text>
                      ),
                    },
                    {
                      label: "Filtros",
                      data: (r: ReportItem) => {
                        const total =
                          Object.values(r.filters?.text ?? {}).filter((v) => v)
                            .length +
                          Object.values(r.filters?.dates ?? {}).filter(
                            (v) => v.from || v.to,
                          ).length;
                        return (
                          <Text color={crm.textFaint} fontSize="sm">
                            {total > 0
                              ? `${total} filtro${total !== 1 ? "s" : ""}`
                              : "—"}
                          </Text>
                        );
                      },
                    },
                    {
                      label: "",
                      data: (r: ReportItem) => (
                        <ButtonGroup>
                          <IconButton
                            variant="ghost"
                            color={crm.brand}
                            _hover={{ bg: crm.brandSoft }}
                            aria-label="Editar"
                            icon={<FaPencilAlt />}
                            onClick={() => handleOpenEdit(r)}
                          />
                          <IconButton
                            variant="ghost"
                            color={crm.brand}
                            _hover={{ bg: crm.brandSoft }}
                            aria-label="Ver detalhes"
                            icon={<FaEye />}
                            onClick={() => handleOpenDetail(r)}
                          />
                          <IconButton
                            variant="ghost"
                            color={crm.danger}
                            _hover={{ bg: "#fef2f2" }}
                            aria-label="Excluir"
                            icon={<FaTrash />}
                            onClick={() => setReportToDelete(r.id)}
                          />
                        </ButtonGroup>
                      ),
                    },
                  ]}
                  page={page}
                  setPage={setPage}
                  get={get}
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  tags={tags}
                  emptyMessage="Nenhum relatório encontrado"
                />
              )}
              <AlertDialog
                isOpen={!!reportToDelete}
                leastDestructiveRef={cancelRef}
                onClose={() => setReportToDelete(null)}
              >
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      Excluir Relatório
                    </AlertDialogHeader>

                    <AlertDialogBody>
                      Tem certeza que deseja excluir este relatório? Esta ação
                      não poderá ser desfeita.
                    </AlertDialogBody>

                    <AlertDialogFooter>
                      <Button
                        ref={cancelRef}
                        onClick={() => setReportToDelete(null)}
                      >
                        Cancelar
                      </Button>

                      <Button colorScheme="red" ml={3} onClick={handleDelete}>
                        Excluir
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
            </Box>
          </Flex>
        </Flex>
      </Layout>

      <NewReportModal
        isOpen={isOpenNew}
        onClose={onCloseNew}
        getProcesses={getProcesses}
        currentCompany={currentCompany}
        currentEmulation={currentEmulation}
        onSave={handleSave}
      />

      <NewReportModal
        isOpen={isOpenEdit}
        onClose={onCloseEdit}
        getProcesses={getProcesses}
        currentCompany={currentCompany}
        currentEmulation={currentEmulation}
        initialName={editingReport?.name}
        initialFields={editingReport?.fields}
        initialFilters={toReportFilters(editingReport?.filters)}
        onSave={handleUpdate}
      />

      {selectedReport && (
        <ReportPreviewModal
          isOpen={isOpenPreview}
          onClose={onClosePreview}
          selectedColumns={selectedReport.fields}
          filters={toReportFilters(selectedReport.filters)}
          name={selectedReport.name}
          getProcesses={getProcesses}
          currentCompany={currentCompany}
          currentEmulation={currentEmulation}
          onSave={onClosePreview}
          hideSave
        />
      )}
    </Box>
  );
};

export default ReportList;
